import { createBrowserAnalytics, type AnalyticsClient } from '@highlight/analytics/browser'

import { getUserFromAccessToken, readAuthSession } from './browser-auth'

/**
 * PostHog for the browser runtime.
 *
 * The renderer UI also ships as a web app (`build:browser`, deployed to
 * Cloudflare Pages), where there is no main process to pin one PostHog device
 * id and no `GlobalStore` to read the account from. posthog-js minting and
 * persisting its own distinct id is the right answer for a single page, so
 * unlike the Electron renderer this passes no `bootstrapDistinctId`.
 *
 * It follows the landing site rather than the Electron renderer on identity:
 * `getUserId` names the signed-in account and nothing calls `identify`.
 * Folding the anonymous session into the account at sign-in needs the
 * `identityTransition` lifecycle, and that needs a sign-out signal this
 * runtime does not have yet.
 */

/**
 * The signed-in account, from the session the browser runtime keeps in
 * localStorage.
 *
 * Read at capture time, outside any try/catch of ours, so a throw would drop
 * the event rather than send it without a user id.
 */
const currentUserId = (): string | undefined => {
  try {
    const session = readAuthSession()
    if (!session) {
      return undefined
    }
    const user = getUserFromAccessToken(session.accessToken, session.provider)
    return typeof user?.id === 'string' ? user.id : undefined
  } catch {
    return undefined
  }
}

/**
 * @param amplitudeDeviceId the shim's own Amplitude id, carried as a property
 * so the two streams can be compared while both run. Never identified with,
 * which would mark the anonymous id identified and make a later sign-in merge
 * illegal. Drop it once Amplitude is retired.
 */
export const createBrowserRuntimeAnalytics = (amplitudeDeviceId: string): AnalyticsClient =>
  createBrowserAnalytics({
    surface: 'browser-app',
    // Vite's mode, so the staging Pages build stays filterable inside the one
    // PostHog project every environment shares, and a dev server does not
    // write into it at all.
    environment: import.meta.env.MODE,
    onEvent: (event) => console.info(`[browser-analytics] would capture ${event.name}`, event.properties),
    getUserId: currentUserId,
    baseProperties: () => ({
      platform: 'browser',
      rendererName: window.__RENDERER_NAME ?? 'unknown',
      version: import.meta.env.VITE_HIGHLIGHT_VERSION ?? 'browser-dev',
      channel: import.meta.env.VITE_HIGHLIGHT_CHANNEL ?? 'latest',
      amplitude_device_id: amplitudeDeviceId,
    }),
    onError: (error, context) => {
      console.error(`[browser-analytics] ${context.sink}/${context.operation} failed`, error)
    },
  })
